import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Eye, ImagePlus } from "lucide-react";
import { api, CATEGORIES, catLabel } from "../../lib/api";
import { formatApiError } from "../../lib/auth";
import { ImageDrop, Thumb, uploadImage } from "./ImageUpload";
import { blocksToText, textToBlocks } from "./bodyFormat";
import { articleState, pubDate, toLocalInput } from "./status";

const CREDIT_KEYS = ["Photography", "Styling", "Hair", "Make-up"];
const EMPTY = { title: "", dek: "", category: "fashion", author: "", tags: "", cover: "", gallery: [], body: "", credits: {} };

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="kicker">{label}</span>
    {hint && <span className="block text-xs text-ink/60 mt-1">{hint}</span>}
    <div className="mt-2">{children}</div>
  </label>
);

export default function AdminEditor() {
  const { slug } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(!!slug);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("now");
  const [when, setWhen] = useState("");
  const [original, setOriginal] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => {
    if (!slug) return undefined;
    let alive = true;
    api.get(`/admin/articles/${slug}`).then(({ data }) => {
      if (!alive) return;
      setF({ ...EMPTY, ...data, tags: (data.tags ?? []).join(", "), body: blocksToText(data.body), credits: data.credits ?? {}, gallery: data.gallery ?? [] });
      const state = articleState(data);
      setMode(state === "live" ? "now" : state === "scheduled" ? "schedule" : "draft");
      setWhen(state === "scheduled" ? toLocalInput(pubDate(data.published)) : "");
      setOriginal(state === "live" ? data.published : null);
      setLoading(false);
    }).catch(() => {
      toast.error("Article not found");
      nav("/admin");
    });
    return () => {
      alive = false;
    };
  }, [slug, nav]);

  const insertBodyImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadImage(file);
      setF((s) => ({ ...s, body: `${s.body.trim()}\n\n![Caption](${url})\n\n` }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    }
    e.target.value = "";
  };

  const save = async (e) => {
    e.preventDefault();
    if (!f.cover) return toast.error("Add a cover photo first");
    if (mode === "schedule" && (!when || new Date(when) <= new Date())) return toast.error("Pick a future date and time");
    setSaving(true);
    const publishAt = mode === "schedule" ? new Date(when).toISOString() : mode === "now" ? original : null;
    const payload = {
      ...f,
      tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
      body: textToBlocks(f.body),
      status: mode === "draft" ? "draft" : "published",
      publish_at: publishAt,
    };
    try {
      const { data } = slug ? await api.put(`/admin/articles/${slug}`, payload) : await api.post("/admin/articles", payload);
      const msg = { now: slug ? "Article updated" : "Article published", schedule: "Article scheduled", draft: "Draft saved" }[mode];
      toast.success(msg);
      qc.invalidateQueries();
      nav(mode === "now" ? `/article/${data.slug}` : "/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  if (loading) return <div className="min-h-screen grid place-items-center kicker">Loading…</div>;

  return (
    <main className="pt-28 pb-24 max-w-[1100px] mx-auto px-5 sm:px-10" data-testid="admin-editor-page">
      <Link to="/admin" className="inline-flex items-center gap-2 kicker hover:text-rose" data-testid="admin-back-link"><ArrowLeft className="h-4 w-4" /> All articles</Link>
      {slug && (
        <Link to={`/admin/preview/${slug}`} className="ml-6 inline-flex items-center gap-2 kicker hover:text-rose" data-testid="editor-preview-link"><Eye className="h-4 w-4" /> Preview saved version</Link>
      )}
      <h1 className="display text-6xl sm:text-7xl mt-4 border-b-2 border-ink pb-6">{slug ? "Edit article" : "New article"}</h1>
      <form onSubmit={save} className="grid lg:grid-cols-12 gap-10 mt-10">
        <div className="lg:col-span-8 space-y-6">
          <Field label="Title"><input required minLength={3} value={f.title} onChange={set("title")} className="admin-input display !text-3xl" data-testid="editor-title" /></Field>
          <Field label="Subtitle (dek)"><textarea rows={2} value={f.dek} onChange={set("dek")} className="admin-input font-serif italic text-lg" data-testid="editor-dek" /></Field>
          <Field label="Text" hint="Blank line = new paragraph · ## Subheading · > Quote — Author · ![caption](photo link)">
            <textarea rows={18} value={f.body} onChange={set("body")} className="admin-input leading-relaxed" data-lenis-prevent data-testid="editor-body" />
          </Field>
          <label className="inline-flex items-center gap-2 kicker cursor-pointer border-2 border-ink px-4 py-2.5 hover:bg-blush transition-colors" data-testid="editor-body-image-btn">
            <ImagePlus className="h-4 w-4" /> Insert photo in text
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={insertBodyImage} data-testid="editor-body-image-input" />
          </label>
          <div>
            <p className="kicker">Photo gallery</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
              {f.gallery.map((src, i) => (
                <Thumb key={`${src}-${i}`} src={src} testId={`editor-gallery-${i}`} onRemove={() => setF((s) => ({ ...s, gallery: s.gallery.filter((_, j) => j !== i) }))} />
              ))}
            </div>
            <div className="mt-3">
              <ImageDrop label="Upload gallery photos (multiple)" multiple testId="editor-gallery-upload" onUploaded={(url) => setF((s) => ({ ...s, gallery: [...s.gallery, url] }))} />
            </div>
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-6">
          <div>
            <p className="kicker">Cover photo</p>
            <div className="mt-3">
              {f.cover ? (
                <Thumb src={f.cover} testId="editor-cover" onRemove={() => setF((s) => ({ ...s, cover: "" }))} />
              ) : (
                <ImageDrop label="Upload cover" testId="editor-cover-upload" onUploaded={(url) => setF((s) => ({ ...s, cover: url }))} />
              )}
            </div>
          </div>
          <Field label="Category">
            <select value={f.category} onChange={set("category")} className="admin-input capitalize" data-testid="editor-category">
              {CATEGORIES.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
            </select>
          </Field>
          <Field label="Author"><input required value={f.author} onChange={set("author")} className="admin-input" data-testid="editor-author" /></Field>
          <Field label="Tags" hint="Comma separated, e.g. skate, london, archive"><input value={f.tags} onChange={set("tags")} className="admin-input" data-testid="editor-tags" /></Field>
          <div className="space-y-3">
            <p className="kicker">Credits</p>
            {CREDIT_KEYS.map((k) => (
              <input key={k} placeholder={k} value={f.credits[k] ?? ""} onChange={(e) => setF((s) => ({ ...s, credits: { ...s.credits, [k]: e.target.value } }))} className="admin-input" data-testid={`editor-credit-${k.toLowerCase()}`} />
            ))}
          </div>
          <div className="border-2 border-ink bg-paper p-4 space-y-3" data-testid="editor-publish-box">
            <p className="kicker">Publishing</p>
            {[["now", "Publish now"], ["schedule", "Schedule for later"], ["draft", "Save as draft"]].map(([v, label]) => (
              <label key={v} className="flex items-center gap-3 cursor-pointer">
                <input type="radio" name="mode" value={v} checked={mode === v} onChange={() => setMode(v)} className="accent-[#FF3D8B] h-4 w-4" data-testid={`editor-mode-${v}`} />
                <span className="text-base">{label}</span>
              </label>
            ))}
            {mode === "schedule" && (
              <input type="datetime-local" value={when} min={toLocalInput(new Date())} onChange={(e) => setWhen(e.target.value)} className="admin-input" data-testid="editor-schedule-input" />
            )}
            {mode === "draft" && <p className="text-sm text-ink/70">Only the newsroom can see drafts.</p>}
          </div>
          <button disabled={saving} className="w-full bg-hot text-ink border-2 border-ink kicker py-4 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid="editor-save-btn">
            {saving ? "Saving…" : { now: slug ? "Save & publish" : "Publish", schedule: "Schedule", draft: "Save draft" }[mode]}
          </button>
        </aside>
      </form>
    </main>
  );
}
